"use client";

import React, { useEffect, useRef, useState, useTransition } from "react";

import Image from "next/image";

import { useEnterprise } from "@/contexts/enterprise-provider";

import { cx } from "@/lib/tailwind-utils";

import { Background } from "./ui/background";

interface LoadingSpinnerProps {
  className?: string;
  size?: number;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  className,
  size = 50,
}) => {
  const enterprise = useEnterprise();
  const faviconUrl = enterprise?.favicon ?? "/logos/decoda-favicon.png";

  if (!enterprise) return null;

  return (
    <div className={cx("transition-all duration-500 ease-out", className)}>
      <div className="animate-spin">
        <Image
          src={faviconUrl}
          alt="Loading"
          width={size}
          height={size}
          style={{ display: "block" }}
        />
      </div>
    </div>
  );
};

interface LoadingProps {
  className?: string;
  size?: number;
  isVisible?: boolean;
}

const Loading: React.FC<LoadingProps> = ({
  className,
  size = 50,
  isVisible = true,
}) => {
  const enterprise = useEnterprise();
  const [_, startTransition] = useTransition();
  const [isExiting, setIsExiting] = useState(false);
  const [shouldRender, setShouldRender] = useState(isVisible);
  const prevVisibleRef = useRef(isVisible);

  // When isVisible becomes false, trigger exit animation
  useEffect(() => {
    if (prevVisibleRef.current && !isVisible && !isExiting) {
      startTransition(() => {
        setIsExiting(true);
        // Delay unmount to allow animation to complete
        setTimeout(() => {
          setShouldRender(false);
        }, 500); // Match transition duration
      });
    } else if (!prevVisibleRef.current && isVisible) {
      // Reset when becoming visible again
      setIsExiting(false);
      setShouldRender(true);
    }

    prevVisibleRef.current = isVisible;
  }, [isVisible, isExiting]);

  if (!enterprise || !shouldRender) return null;

  return (
    <div
      className={cx(
        // transform-gpu makes this a containing block for `fixed` descendants,
        // so the Background canvas can never paint outside the overlay
        "fixed inset-0 z-[9999] transform-gpu bg-background transition-opacity duration-500",
        isExiting && "opacity-0",
        className,
      )}
    >
      <Background animate={true} showDots={true} />
      <div
        className={cx(
          "absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ease-out",
          isExiting && "opacity-0",
        )}
      >
        <LoadingSpinner size={size} />
      </div>
    </div>
  );
};

export default Loading;
